/**
 * TopBarPractice — the practice switcher in the global bar, top right after the search (the design's TopBar
 * `practices`). The practice picker exists only on home and Calendar, so this renders only on those two routes
 * (`/dashboard`, the home for everyone since 21 Oct, and `/calendar`) and is null everywhere else, wherever the shell
 * mounts it.
 *
 * It shows the app-wide active practice (`user.practiceId`, what the api-client's PracticeId header carries) and a
 * choice writes both that and the calendar's persisted selection (scheduling store), so the Calendar and every other
 * page agree. The list is the practitioner's own practices (ForPractitioner), each row with its picture, address and
 * today's hours. No "Practice page" button: the app has no practice settings route (`/settings/practices/:id` is a 404
 * in `src/app/router.tsx`), so the button would lead nowhere.
 * The Practitioner app has no nav guard: the switch is immediate and queries re-key on the new practice.
 */
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/auth/auth-store';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import { usePractitionerPractices } from '@/scheduling/services/use-practitioner-practices';
import { todayHoursLine } from '@/scheduling/lib/practice-hours';
import { PracticeSwitcher, type PracticeSwitcherPractice } from './practice-switcher';

/** The routes that carry a practice picker: home (`/dashboard`; `/today` redirects there) and Calendar. */
const PRACTICE_PICKER_ROUTES = ['/dashboard', '/calendar'];

export function TopBarPractice() {
  const { pathname } = useLocation();
  const onPickerRoute = PRACTICE_PICKER_ROUTES.some((path) => matchPath({ path, end: true }, pathname));
  const user = useAuthStore((s) => s.user);
  const setActivePractice = useAuthStore((s) => s.setActivePractice);
  const setSelectedPracticeId = useSchedulingStore((s) => s.setSelectedPracticeId);
  const selfId = user?.practitionerId ?? user?.id;
  const { data: practiceList } = usePractitionerPractices(selfId);
  const practices = useMemo<PracticeSwitcherPractice[]>(() => {
    const seen = new Set<string>();
    return (practiceList ?? [])
      .filter((p): p is typeof p & { practiceSetupId: string } => {
        if (!p.practiceSetupId || seen.has(p.practiceSetupId)) return false;
        seen.add(p.practiceSetupId);
        return true;
      })
      .map((p) => ({
        id: p.practiceSetupId,
        name: p.practiceName ?? 'Practice',
        imageUrl: p.practiceImageURL,
        address: p.address,
        isPrimary: !!p.isPrimary,
        hoursLine: todayHoursLine(p.workingHours),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [practiceList]);
  if (!onPickerRoute || practices.length === 0) return null;
  return (
    <div className="shell-topbar-practice">
      <PracticeSwitcher
        practices={practices}
        practiceId={user?.practiceId}
        onPractice={(id) => {
          setSelectedPracticeId(id);
          setActivePractice({ practiceSetupId: id, practiceName: practices.find((p) => p.id === id)?.name ?? null });
        }}
      />
    </div>
  );
}
